import isNil from 'lodash/isNil'
import * as React from 'react'
import {fromMaybe} from '@freckle/maybe'
import {exhaustive} from '@freckle/exhaustive'
import {type ColumnDef} from '@tanstack/react-table'

import {
  type StudentMathLevelInfoT,
  type StudentElaLevelInfoT,
  type StudentLevelInfoT
} from '@frontrow/console/src/models/student-level-info'
import DataTable from '@frontrow/console/src/components/common/data-table'

type Props = {
  isLoading: boolean
  studentLevelData: StudentLevelInfoT
}

export default function CsvStudentLevelPreviewTable(props: Props): React.ReactElement {
  const {isLoading, studentLevelData} = props

  switch (studentLevelData.uploadType) {
    case 'Math':
      const mathColumns: ColumnDef<StudentMathLevelInfoT, unknown>[] = [
        {
          id: 'schoolName',
          header: 'School',
          accessorFn: sl => sl.schoolName
        },
        {
          id: 'studentSisId',
          header: 'SIS ID',
          accessorFn: sl => sl.studentSisId
        },
        {
          id: 'studentName',
          header: 'Student',
          accessorFn: sl => sl.studentName
        },
        {
          id: 'domainName',
          header: 'Domain',
          accessorFn: sl =>
            hasPlacementInSystem(sl) ? sl.rlDomain?.name : 'Placement outside of district system'
        },
        {
          id: 'standardName',
          header: 'Standard',
          accessorFn: sl => (hasPlacementInSystem(sl) ? sl.rlStandard?.name : '')
        }
      ]
      return (
        <DataTable
          columns={mathColumns}
          data={fromMaybe(() => [], studentLevelData.mathData)}
          isLoading={isLoading}
          defaultPageSize={10}
          updateUrlOnFilter={false}
        />
      )
    case 'Ela':
      const elaColumns: ColumnDef<StudentElaLevelInfoT, unknown>[] = [
        {
          id: 'studentId',
          header: 'Student ID',
          accessorFn: sl => sl.studentId
        },
        {
          id: 'sisId',
          header: 'SIS ID',
          accessorFn: sl => sl.sisId
        },
        {
          id: 'readingLevel',
          header: 'Reading Level',
          accessorFn: sl => sl.readingLevel
        }
      ]
      return (
        <DataTable
          columns={elaColumns}
          data={fromMaybe(() => [], studentLevelData.elaData)}
          isLoading={isLoading}
          defaultPageSize={10}
          updateUrlOnFilter={false}
        />
      )
    default:
      return exhaustive(studentLevelData)
  }
}

const hasPlacementInSystem = ({rlDomain, rlStandard}: StudentMathLevelInfoT): boolean =>
  !isNil(rlDomain) && !isNil(rlStandard)
